import { useMemo, useState } from 'react';
import styles from './ContactMessages.module.scss';
import TablePagination from '../../Molecules/Pagination/TablePagination';
import ContactMessagesFilters from '../../Molecules/ContactMessages/ContactMessagesFilters';
import useDebouncedValue from '../../Molecules/ContactMessages/useDebouncedValue';
import Loader from '../../Molecules/Loader/Loader';
import DeletePopup from '../../Molecules/Custom/DeletePopup';
import MessageDetailsModal from '../../Molecules/ContactMessages/MessageDetailsModal';
import {
  useFetchContactMessages,
  useDeleteContactMessage,
  useMarkContactMessageRead,
} from '../../../Hooks/ContactMessages';
import { useFetchContactSettings } from '../../../Hooks/ContactSettings';
import { useToast } from '../../Molecules/Custom/CustomToaster/Toast';
import { toast } from 'react-toastify';

/**
 * Contact messages sent from the public website's /academics/contact-us form.
 *
 * Browse every message, open one in full, delete one. A message is a record of
 * what a member of the public wrote, so it is read-only apart from deletion and
 * the `isRead` flag — there is nothing here anyone should be editing.
 *
 * THE TABLE AND ITS FILTERS, NOTHING ELSE. The recipients panel that used to
 * sit above the list has moved to a screen of its own — `/home/contact-settings`
 * — now that the backend serves full CRUD for it. Configuration an editor
 * maintains and an inbox they read are two jobs, and they were competing for
 * the top of this page.
 *
 * The department list is still READ here, from the same endpoint that screen
 * writes to (`GET /api/ContactUs/Settings`). Two things need it: the «الجهة
 * المعنية» filter, which offers the known addresses rather than asking an
 * operator to remember them, and the table cell, which shows a department name
 * in place of a bare address. It replaces a hard-coded copy of the same six
 * departments that had to be kept in step with the website by hand.
 */
const DEFAULT_PAGE_SIZE = 10;

const ContactMessagesPage = () => {
  const [search, setSearch] = useState('');
  const [openMessage, setOpenMessage] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  // ONE-BASED throughout: `TablePagination` renders and emits 1-based page
  // numbers, and the DPA API's first page is also 1 (`/ContactUs/1/10`). So the
  // value passes straight through with NO off-by-one conversion anywhere - the
  // number in the pager is the number in the URL.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // fromDate / toDate go to the API as query params.
  const [filters, setFilters] = useState({ fromDate: '', toDate: '' });

  // Column criteria from the «تصفية حسب / القيمة» builder: [{ field, value }],
  // one per column, AND semantics. Values arrive here already COMMITTED (the
  // bar's «إضافة» button), so nothing in this array is ever half-typed and no
  // debounce is needed for it.
  const [fieldFilters, setFieldFilters] = useState([]);

  const customizedToast = useToast();

  // Only the TYPED search is debounced. Dates and committed criteria are
  // discrete choices - delaying those would just make the screen feel slow.
  const debouncedSearch = useDebouncedValue(search);

  // MULTI-COLUMN FILTERING IS SERVER-SIDE since 2026-09-23: the backend ships
  // indexed `Filters[i].FilterBy/FilterValue` binding with AND semantics (the
  // wire format is documented in Hooks/ContactMessages.js). Every chip goes to
  // the server, the server intersects and paginates, and `totalCount` drives
  // the pager in every case. The interim client-side intersection mode
  // (fetch-all via `pageSize: 0`) that bridged the gap before the backend
  // change is deleted - one code path, no local pagination.
  //
  // Pagination in the path, everything else as query filters - all built by
  // `services/apiClient.js`, the only place that assembles a URL.
  const { data, isLoading, isError, error, refetch } = useFetchContactMessages({
    page,
    pageSize,
    searchText: debouncedSearch,
    fromDate: filters.fromDate,
    toDate: filters.toDate,
    filters: fieldFilters,
  });
  // Reference data for the filter dropdown and the department column. A
  // failure here must not take the table down with it — the messages are the
  // point of the screen and every one of them still renders, just under its
  // raw `receiverEmail`. Hence no `isError` branch: `rows` falls back to [].
  const { data: recipients } = useFetchContactSettings();

  const { mutate: deleteMessage, isLoading: isDeleting } =
    useDeleteContactMessage();
  const { mutate: markRead } = useMarkContactMessageRead();

  // `apiGetList` guarantees an array (contract 5.1: `items` is never null).
  const messages = useMemo(() => data?.items ?? [], [data]);

  /**
   * `receiverEmail` -> department name.
   *
   * Lower-cased on both sides because the backend matches an address
   * case-insensitively, so a message stored with `IT@…` must still find the row
   * configured as `it@…`.
   *
   * An address that is no longer in the list — a department that was deleted or
   * renamed since the message arrived — resolves to nothing, and the caller
   * falls back to showing the raw address. That is the honest answer: the
   * message really did go to an address the site no longer knows about, and
   * inventing a name for it would hide exactly the case an operator needs to
   * see.
   */
  const departmentNameFor = useMemo(() => {
    const byEmail = new Map(
      (recipients ?? [])
        .filter((row) => row?.email)
        .map((row) => [row.email.toLowerCase(), row.title])
    );
    return (email) => (email ? byEmail.get(email.toLowerCase()) : undefined);
  }, [recipients]);

  // Contract 5.1: `totalCount` reflects the FILTERED set, so it is what drives
  // the pager - not the length of the current page.
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / Math.max(1, pageSize)));

  // Only the rows on screen can be counted; the server does not return an
  // unread total. Labelled accordingly in the header so it cannot be mistaken
  // for a count across every page.
  const unreadOnPage = messages.filter((row) => !row?.isRead).length;

  /**
   * Any change to the filter or the page size invalidates the current page
   * number: a search that narrows 120 rows to 3 leaves an operator stranded on
   * page 5 of a one-page result, staring at an empty table.
   */
  const handleSearch = (value) => {
    setSearch(value);
    setPage(1);
  };

  /** Any filter change invalidates the current page number - see above. */
  const handleFilterChange = (patch) => {
    setFilters((current) => ({ ...current, ...patch }));
    setPage(1);
  };

  /**
   * One criterion per column: adding a field that already has a chip replaces
   * it. The bar disables used fields so this is belt-and-braces, not a path an
   * operator can normally reach.
   */
  const handleAddFilter = (field, value) => {
    setFieldFilters((current) => [
      ...current.filter((criterion) => criterion.field !== field),
      { field, value },
    ]);
    setPage(1);
  };

  const handleRemoveFilter = (field) => {
    setFieldFilters((current) =>
      current.filter((criterion) => criterion.field !== field)
    );
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearch('');
    setFilters({ fromDate: '', toDate: '' });
    setFieldFilters([]);
    setPage(1);
  };

  const handlePageSize = (size) => {
    setPageSize(size);
    setPage(1);
  };

  /**
   * Opening a message marks it read. The local row is updated straight away so
   * the badge clears immediately; the refetch reconciles with the server. A
   * failure is deliberately silent — the editor is reading the message either
   * way, and a toast about a bookkeeping flag would be noise over the thing
   * they actually came to do.
   */
  const handleOpen = (row) => {
    setOpenMessage(row);
    if (row?.isRead || !row?.id) return;

    markRead(row.id, {
      onSuccess: () => refetch(),
      onError: () => {},
    });
  };

  const handleDelete = () => {
    const id = pendingDelete?.id;
    if (!id) return;

    deleteMessage(id, {
      onSuccess: () => {
        customizedToast.open('تم حذف الرسالة بنجاح');
        setPendingDelete(null);
        setOpenMessage(null);
        refetch();
      },
      onError: (error) => {
        toast.error(error?.message ?? 'تعذّر حذف الرسالة');
        setPendingDelete(null);
      },
    });
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    // A fixed Gregorian order, so a date never reads backwards in the RTL table.
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  };

  return (
    <div className={styles['messages-page']}>
      {/* Not `NewsTopHeader`: that component always renders a "create" button,
          and this screen has nothing to create - messages arrive from the
          website. Its `title` prop also REPLACES the search box rather than
          sitting beside it, so a heading and a search field cannot coexist
          there. A local header is simpler than adding two flags to a component
          fifteen other screens rely on. */}
      <div className={styles['page-head']}>
        <div className={styles['page-heading']}>
          <h2 className={styles['page-title']}>رسائل التواصل</h2>
          {unreadOnPage > 0 && (
            <span className={styles['unread-pill']}>
              {unreadOnPage} غير مقروءة في هذه الصفحة
            </span>
          )}
          {totalCount > 0 && (
            <span className={styles['total-pill']}>
              الإجمالي: {totalCount}
            </span>
          )}
        </div>
      </div>

      <ContactMessagesFilters
        search={search}
        onSearch={handleSearch}
        fromDate={filters.fromDate}
        toDate={filters.toDate}
        fieldFilters={fieldFilters}
        onAddFilter={handleAddFilter}
        onRemoveFilter={handleRemoveFilter}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        recipients={recipients ?? []}
      />

      <div className={styles['table-container']}>
        {isLoading ? (
          <Loader />
        ) : isError ? (
          // A 404 here means the contract-shaped list route
          // (`/api/ContactUs/{page}/{pageSize}`) is not deployed, which is a
          // backend gap, not something retrying will fix. Saying so beats
          // "please try again", which sends an operator round a loop forever.
          <p className={styles.empty}>
            {error?.status === 404
              ? 'واجهة جلب الرسائل (‎/api/ContactUs/{page}/{pageSize}‎) غير متوفرة على الخادم بعد. يرجى إبلاغ فريق الواجهة الخلفية.'
              : (error?.message ?? 'تعذّر تحميل الرسائل. يرجى المحاولة مرة أخرى.')}
          </p>
        ) : messages.length === 0 ? (
          <p className={styles.empty}>
            {search.trim() ||
            filters.fromDate ||
            filters.toDate ||
            fieldFilters.length > 0
              ? 'لا توجد رسائل مطابقة للفلاتر المحددة.'
              : 'لا توجد رسائل حتى الآن.'}
          </p>
        ) : (
          <div className={styles['table-scroll']}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope='col'>الرقم المرجعي</th>
                  <th scope='col'>الحالة</th>
                  <th scope='col'>المرسل</th>
                  <th scope='col'>البريد الإلكتروني</th>
                  <th scope='col'>الجهة المعنية</th>
                  <th scope='col'>الموضوع</th>
                  <th scope='col'>التاريخ</th>
                  <th scope='col'>إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((row) => (
                  <tr
                    key={row?.id}
                    className={row?.isRead ? undefined : styles.unread}
                  >
                    <td>
                      {/* The number a visitor quotes when they follow up, so it
                          leads the row. Western numerals inside an RTL table
                          need their own direction or a multi-digit value can
                          render with its digits reordered. */}
                      {row?.referenceNumber != null ? (
                        <span className={`${styles['reference']} ${styles.ltr}`}>
                          #{row.referenceNumber}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>
                      {/* Text, not colour alone - WCAG 1.4.1. */}
                      <span
                        className={
                          row?.isRead ? styles['badge-read'] : styles.badge
                        }
                      >
                        {row?.isRead ? 'مقروءة' : 'جديدة'}
                      </span>
                    </td>
                    <td>{row?.fullName || '—'}</td>
                    <td className={styles.ltr}>{row?.email || '—'}</td>
                    <td>
                      {departmentNameFor(row?.receiverEmail) ||
                        row?.receiverEmail ||
                        '—'}
                    </td>
                    <td className={styles.subject}>{row?.subject || '—'}</td>
                    <td className={styles.ltr}>{formatDate(row?.createdOn)}</td>
                    <td>
                      <div className={styles['row-actions']}>
                        <button
                          type='button'
                          className={styles['link-button']}
                          onClick={() => handleOpen(row)}
                        >
                          عرض
                        </button>
                        <button
                          type='button'
                          className={`${styles['link-button']} ${styles.danger}`}
                          onClick={() => setPendingDelete(row)}
                        >
                          حذف
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Rendered only when there is genuinely more than one page. A pager
          showing a lone "1" is chrome that tells an operator nothing, and the
          page-size box invites a reload for no benefit. `page` is one-based
          here and one-based on the wire - no conversion. */}
      {!isLoading && !isError && totalPages > 1 && (
        <div className={styles['pager']}>
          <TablePagination
            page={page}
            itemsPerPage={pageSize}
            totalPages={totalPages}
            setPage={setPage}
            setPageSize={handlePageSize}
          />
        </div>
      )}

      {openMessage && (
        <MessageDetailsModal
          message={openMessage}
          formatDate={formatDate}
          departmentName={departmentNameFor(openMessage?.receiverEmail)}
          onClose={() => setOpenMessage(null)}
          onDelete={() => setPendingDelete(openMessage)}
        />
      )}

      {pendingDelete && (
        <DeletePopup
          title='الرسالة'
          isLoading={isDeleting}
          handleOnClose={() => setPendingDelete(null)}
          handleDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default ContactMessagesPage;
